import { useState } from 'react'
import type { AiCisoConversationMode, ConversationModeOption, ConversationModeRuntime } from './types'
import { Button } from '../ui/Button'

type TranslateFn = (key: string, fallback?: string) => string

const REASON_FALLBACKS: Record<string, string> = {
  LOCAL_INFERENCE_NOT_VERIFIED: 'Local model inference is not verified yet',
  LOCAL_PROVIDER_NOT_CONFIGURED: 'No local AI provider configured',
  EXTERNAL_PROVIDER_UNAVAILABLE: 'No organization-approved external provider available',
  EXTERNAL_POLICY_BLOCKED: 'External AI blocked by organization policy',
  NO_ORG_APPROVED_PROVIDER: 'No organization-approved external provider',
  ORGANIZATION_POLICY_DENIED: 'Provider or model not allowed by organization AI policy',
  MODE_NOT_IMPLEMENTED: 'This mode is not available yet',
  INVALID_MODE: 'Invalid conversation mode',
  NO_LLM: 'External AI disabled by NO_LLM policy',
  LOCAL_ONLY: 'External AI disabled by LOCAL_ONLY policy',
}

function reasonLabel(reason: string | null | undefined, t: TranslateFn): string {
  if (!reason) return ''
  return t(`complianceCenterUi.aiCiso.modeReason.${reason}`, REASON_FALLBACKS[reason] || reason)
}

function resolveSimpleMode(value: string | null | undefined): 'INTERNAL' | 'EXTERNAL' {
  const v = String(value || '').toUpperCase()
  return v === 'EXTERNAL_AI_RAG' ? 'EXTERNAL' : 'INTERNAL'
}

export function DefendSphereAiModeSelector({
  runtime,
  value,
  disabled = false,
  busy = false,
  onChange,
  onGrantExternalConsent,
  t,
}: {
  runtime: ConversationModeRuntime | null
  value: string | null | undefined
  disabled?: boolean
  busy?: boolean
  onChange: (mode: AiCisoConversationMode) => void
  onGrantExternalConsent?: () => Promise<void> | void
  t: TranslateFn
}) {
  const modes: ConversationModeOption[] = runtime?.modes?.length ? runtime.modes : []
  const standards = modes.find((m) => m.mode === 'STANDARDS_RAG')
  const internalRag = modes.find((m) => m.mode === 'INTERNAL_AI_RAG')
  const external = modes.find((m) => m.mode === 'EXTERNAL_AI_RAG')

  const internalOk = Boolean(standards?.selectable || internalRag?.selectable)
  const externalOk = Boolean(external?.selectable)
  const consentSatisfied = Boolean(runtime?.consentSatisfied)
  const selectedSimple = resolveSimpleMode(value || runtime?.conversationMode || runtime?.defaultMode)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const pickInternalMode = (): AiCisoConversationMode => {
    if (internalRag?.selectable) return 'INTERNAL_AI_RAG'
    return 'STANDARDS_RAG'
  }

  const selectInternal = () => {
    if (disabled || busy || !internalOk) return
    onChange(pickInternalMode())
  }

  const selectExternal = () => {
    if (disabled || busy || !externalOk) return
    if (!consentSatisfied) {
      setConfirmOpen(true)
      return
    }
    onChange('EXTERNAL_AI_RAG')
  }

  const confirmExternal = async () => {
    setConfirmOpen(false)
    // grantExternalConsent PATCH already sets EXTERNAL_AI_RAG + consent metadata
    if (onGrantExternalConsent) {
      await onGrantExternalConsent()
      return
    }
    onChange('EXTERNAL_AI_RAG')
  }

  const externalBlockedReason =
    !externalOk
      ? reasonLabel(external?.reason || 'EXTERNAL_PROVIDER_UNAVAILABLE', t)
      : null

  return (
    <div className="ds-ai-mode-selector" role="group" aria-label={t('complianceCenterUi.aiCiso.modeLabel', 'Conversation mode')}>
      <span className="ds-ai-mode-selector__label">{t('complianceCenterUi.aiCiso.modeLabel', 'Mode')}</span>
      <div className="ds-ai-mode-selector__toggle" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Button
          type="button"
          size="small"
          color={selectedSimple === 'INTERNAL' ? 'primary' : 'outline'}
          disabled={disabled || busy || !internalOk}
          onClick={selectInternal}
          data-testid="ai-mode-internal"
        >
          {t('complianceCenterUi.aiCiso.modeInternalSimple', 'Internal AI / RAG')}
        </Button>
        <Button
          type="button"
          size="small"
          color={selectedSimple === 'EXTERNAL' ? 'primary' : 'outline'}
          disabled={disabled || busy || !externalOk}
          onClick={selectExternal}
          data-testid="ai-mode-external"
        >
          {t('complianceCenterUi.aiCiso.modeExternalSimple', 'External AI')}
        </Button>
      </div>
      <div className="ds-ai-mode-selector__meta" aria-live="polite">
        {selectedSimple === 'INTERNAL' ? (
          <span>
            {t(
              'complianceCenterUi.aiCiso.modeInternalHint',
              'Uses DefendSphere knowledge and organization data. Does not call external AI providers.',
            )}
          </span>
        ) : null}
        {selectedSimple === 'EXTERNAL' && externalOk ? (
          <span>
            {consentSatisfied
              ? t(
                  'complianceCenterUi.aiCiso.modeExternalConsentOk',
                  'External AI enabled for this conversation. Consent already granted.',
                )
              : t(
                  'complianceCenterUi.aiCiso.modeExternalNeedsConsentOnce',
                  'External AI requires a one-time confirmation for this conversation.',
                )}
          </span>
        ) : null}
        {externalBlockedReason ? (
          <span className="ds-ai-mode-selector__reason">{externalBlockedReason}</span>
        ) : null}
      </div>

      {confirmOpen ? (
        <div
          className="ds-ai-mode-selector__consent"
          role="dialog"
          aria-modal="true"
          data-testid="ai-external-consent-dialog"
          style={{
            marginTop: 8,
            padding: 12,
            border: '1px solid var(--ds-border, #cbd5e1)',
            borderRadius: 8,
            background: 'var(--ds-surface, #fff)',
          }}
        >
          <p style={{ margin: '0 0 8px', fontSize: 13 }}>
            {t(
              'complianceCenterUi.aiCiso.externalConsentPrompt',
              'When using external AI, your request and necessary context may be sent to the selected AI provider to generate a response. Continue?',
            )}
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button type="button" size="small" color="primary" onClick={() => void confirmExternal()}>
              {t('complianceCenterUi.aiCiso.externalConsentAllow', 'Allow and continue')}
            </Button>
            <Button type="button" size="small" color="outline" onClick={() => setConfirmOpen(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
